import type { ContainerType } from "~/container";
import type { IApi } from "~/interfaces/IApi";
import type { IReportService } from "@saif-reporting/domain/interfaces/services/IReportService.js";
import { ReportDto } from "@saif-reporting/domain/dtos/models/ReportDto.js";
import type { Paginated } from "@saif-reporting/domain/dtos/Paginated.js";
import type {
  ReportListQuery,
  ReportCreateRequest,
  ReportEscalateRequest,
  ReportAddRemarkRequest,
  ShareReportRequest,
} from "@saif-reporting/domain/dtos/reportDtos.js";
import { plainToInstance } from "class-transformer";
import type { MediaDto } from "@saif-reporting/domain/dtos/models/MediaDto.js";

export class ReportService implements IReportService {
  private readonly baseApi: IApi;

  constructor({ baseApi }: ContainerType) {
    this.baseApi = baseApi;
  }

  async getAll(query: ReportListQuery): Promise<Paginated<ReportDto>> {
    const reports = await this.baseApi.$fetch<Paginated<ReportDto>>("/reports", {
      query: { ...query, startDate: query.startDate?.toISOString(), endDate: query.endDate?.toISOString() },
    });

    return { ...reports, records: plainToInstance(ReportDto, reports.records) };
  }

  async getById(id: number): Promise<ReportDto> {
    const report = await this.baseApi.$fetch<ReportDto>(`/reports/${id}`);
    return plainToInstance(ReportDto, report);
  }

  async update(id: number, dto: ReportCreateRequest): Promise<ReportDto> {
    return await this.baseApi.$fetch<ReportDto>(`/reports/${id}`, {
      method: "patch",
      body: dto,
    });
  }

  async delete(id: number): Promise<ReportDto> {
    return await this.baseApi.$fetch<ReportDto>(`/reports/${id}`, {
      method: "delete",
    });
  }

  async escalateReport(id: number, dto: ReportEscalateRequest): Promise<void> {
    await this.baseApi.$fetch(`/reports/${id}/escalate`, {
      method: "patch",
      body: dto,
    });
  }

  async addRemarks(id: number, dto: ReportAddRemarkRequest): Promise<void> {
    await this.baseApi.$fetch(`/reports/${id}/add-remarks`, {
      method: "patch",
      body: dto,
    });
  }

  async share(reportId: number, dto: ShareReportRequest): Promise<{ token: string }> {
    const results = await this.baseApi.$fetch<{ token: string }>(`/reports/${reportId}/share`, {
      method: "post",
      body: dto,
    });

    return results;
  }

  async getSharedReport(token: string): Promise<ReportDto> {
    const results = await this.baseApi.$fetch<ReportDto>(`/reports/shared-report/${token}`, {});

    return plainToInstance(ReportDto, results);
  }

  async saveMedia(reportId: number, file: File): Promise<MediaDto> {
    const formdata = new FormData();
    formdata.append("file", file);
    const results = await this.baseApi.$fetch<MediaDto>(`/reports/${reportId}/save-media`, {
      method: "post",
      body: formdata,
    });

    return results;
  }

  async deleteMedia(reportId: number, mediaId: number): Promise<MediaDto> {
    return await this.baseApi.$fetch<MediaDto>(`/reports/${reportId}/${mediaId}`, {
      method: "delete",
    });
  }
}
